(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.KiiveStudioTools = root.KiiveStudioTools || {};
    root.KiiveStudioTools.tapTempo = api;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var INACTIVITY_MS = 3000;
  var MIN_INTERVAL_MS = 60;
  var MAX_INTERVALS = 8;
  var MIN_INTERVALS = 3;
  var DISPLAY_DECIMALS = 1;
  var CHANGE_RUN_INTERVALS = 3;
  var CHANGE_RUN_COHERENCE = 0.12;
  var CHANGE_RUN_SHIFT = 0.2;

  function freezeIntervals(intervals) {
    return Object.freeze(intervals.slice());
  }

  function createState(values) {
    var state = values || {};

    return Object.freeze({
      lastTapAt: state.lastTapAt === undefined ? null : state.lastTapAt,
      intervals: freezeIntervals(state.intervals || []),
      stableBpm: state.stableBpm === undefined ? null : state.stableBpm,
      stableInterval: state.stableInterval === undefined ? null : state.stableInterval,
      event: state.event || 'empty',
      recentered: Boolean(state.recentered)
    });
  }

  function median(values) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new TypeError('At least one interval is required.');
    }

    var sorted = values.slice().sort(function (a, b) {
      return a - b;
    });
    var midpoint = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 1) {
      return sorted[midpoint];
    }

    return (sorted[midpoint - 1] + sorted[midpoint]) / 2;
  }

  function medianAbsoluteDeviation(values, center) {
    var reference = center === undefined ? median(values) : center;
    return median(values.map(function (value) {
      return Math.abs(value - reference);
    }));
  }

  function estimateInterval(intervals) {
    if (!Array.isArray(intervals) || intervals.length < MIN_INTERVALS) {
      return null;
    }

    var center = median(intervals);
    var deviation = medianAbsoluteDeviation(intervals, center);
    var tolerance = Math.min(
      center * 0.35,
      Math.max(center * 0.18, deviation * 3, 30)
    );
    var inliers = intervals.filter(function (interval) {
      return Math.abs(interval - center) <= tolerance;
    });

    if (inliers.length < MIN_INTERVALS) {
      return null;
    }

    var total = inliers.reduce(function (sum, interval) {
      return sum + interval;
    }, 0);

    return Object.freeze({
      interval: total / inliers.length,
      inlierCount: inliers.length,
      median: center,
      mad: deviation,
      tolerance: tolerance
    });
  }

  function isCoherentChangeRun(intervals, stableInterval) {
    if (
      typeof stableInterval !== 'number' ||
      !Number.isFinite(stableInterval) ||
      intervals.length < CHANGE_RUN_INTERVALS
    ) {
      return false;
    }

    var run = intervals.slice(-CHANGE_RUN_INTERVALS);
    var runMedian = median(run);
    var coherent = run.every(function (interval) {
      return Math.abs(interval - runMedian) <= runMedian * CHANGE_RUN_COHERENCE;
    });
    var shifted = Math.abs(runMedian - stableInterval) >= stableInterval * CHANGE_RUN_SHIFT;

    return coherent && shifted;
  }

  function startSequence(timestamp, event) {
    return createState({
      lastTapAt: timestamp,
      event: event
    });
  }

  function recordTap(state, timestamp) {
    var current = state || createState();

    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
      throw new TypeError('A finite monotonic timestamp is required.');
    }

    if (current.lastTapAt === null) {
      return startSequence(timestamp, 'started');
    }

    if (timestamp <= current.lastTapAt) {
      throw new RangeError('Tap timestamps must increase.');
    }

    var interval = timestamp - current.lastTapAt;

    if (interval >= INACTIVITY_MS) {
      return startSequence(timestamp, 'restarted');
    }

    if (interval < MIN_INTERVAL_MS) {
      return createState({
        lastTapAt: current.lastTapAt,
        intervals: current.intervals,
        stableBpm: current.stableBpm,
        stableInterval: current.stableInterval,
        event: 'duplicate'
      });
    }

    var intervals = current.intervals.concat(interval).slice(-MAX_INTERVALS);
    var recentered = isCoherentChangeRun(intervals, current.stableInterval);

    if (recentered) {
      intervals = intervals.slice(-CHANGE_RUN_INTERVALS);
    }

    var estimate = estimateInterval(intervals);
    var stableInterval = current.stableInterval;
    var stableBpm = current.stableBpm;

    if (estimate) {
      stableInterval = estimate.interval;
      stableBpm = 60000 / stableInterval;
    }

    return createState({
      lastTapAt: timestamp,
      intervals: intervals,
      stableBpm: stableBpm,
      stableInterval: stableInterval,
      event: recentered ? 'recentered' : estimate ? 'estimated' : 'acquiring',
      recentered: recentered
    });
  }

  function formatBpm(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new TypeError('A positive finite BPM value is required.');
    }

    var rounded = Number(value.toFixed(DISPLAY_DECIMALS));
    return String(Object.is(rounded, -0) ? 0 : rounded);
  }

  function getTapCount(state) {
    if (!state || state.lastTapAt === null) {
      return 0;
    }

    return state.intervals.length + 1;
  }

  return Object.freeze({
    INACTIVITY_MS: INACTIVITY_MS,
    MIN_INTERVAL_MS: MIN_INTERVAL_MS,
    MAX_INTERVALS: MAX_INTERVALS,
    MIN_INTERVALS: MIN_INTERVALS,
    DISPLAY_DECIMALS: DISPLAY_DECIMALS,
    CHANGE_RUN_INTERVALS: CHANGE_RUN_INTERVALS,
    CHANGE_RUN_COHERENCE: CHANGE_RUN_COHERENCE,
    CHANGE_RUN_SHIFT: CHANGE_RUN_SHIFT,
    createState: createState,
    recordTap: recordTap,
    estimateInterval: estimateInterval,
    median: median,
    medianAbsoluteDeviation: medianAbsoluteDeviation,
    isCoherentChangeRun: isCoherentChangeRun,
    formatBpm: formatBpm,
    getTapCount: getTapCount
  });
});
